import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { Coins, TrendingUp, Info } from 'lucide-react';
import axios from 'axios';
import { TokenCard } from '../components/TokenCard';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function TokensPage() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTracks = async () => {
      try {
        const response = await axios.get(`${API_URL}/tracks`);
        setTracks(response.data);
      } catch (error) {
        logger.error('Failed to fetch tracks:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTracks();
  }, []);

  const totalAvailable = tracks.reduce((acc, t) => acc + t.available_tokens, 0);
  const totalValue = tracks.reduce((acc, t) => acc + (t.token_price * t.available_tokens), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="main-content pt-24" data-testid="tokens-page">
      <div className="container py-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
              <Coins className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Token Marketplace</h1>
              <p className="text-gray-400">Fractional music ownership</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-400 text-sm">Available Tokens</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-4 h-4 text-gray-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Total percentage of ownership available across all tracks</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-3xl font-bold token-data">{totalAvailable}%</p>
            <p className="text-xs text-gray-500 mt-1">across {tracks.length} tracks</p>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-400 text-sm">Total Market Value</p>
              <TrendingUp className="w-4 h-4 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-purple-400">${totalValue.toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">of available tokens</p>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-400 text-sm">Avg Token Price</p>
              <Coins className="w-4 h-4 text-cyan-400" />
            </div>
            <p className="text-3xl font-bold token-data">
              ${(tracks.reduce((acc, t) => acc + t.token_price, 0) / tracks.length).toFixed(2)}
            </p>
            <p className="text-xs text-gray-500 mt-1">per 1% ownership</p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="glass-card p-6 mb-12 border-l-4 border-cyan-500">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Info className="w-5 h-5 text-cyan-400" />
            How Fractional Ownership Works
          </h3>
          <p className="text-gray-400 text-sm">
            Each track is divided into 100 tokens representing 1% ownership each. 
            When you purchase tokens, you own a percentage of the track's royalty rights. 
            As the track earns revenue from streams and sales, token holders receive proportional earnings.
          </p>
        </div>

        {/* Token Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tracks.map((track) => (
            <TokenCard key={track.id} track={track} />
          ))}
        </div>
      </div>
    </div>
  );
}
